import { CustomType, IServiceTextChat, PeerMsgType } from "@obsbot/remo-services";
import { SideEffectManager } from "side-effect-manager";
import { create } from "zustand"; // 修正：从 zustand 导入 create
import { useGlobalStore } from "../global-store";
export interface InitChatStoreConfig {
    rtm: IServiceTextChat;
}

export interface ChatStoreState {
    /**
     * 副作用管理
     */
    sideEffect: SideEffectManager;
    /**
     * 私信对象UUID
     */
    readonly toUserUUID: string;
    /**
     * 用户UUID
     */
    readonly userUUID: string;

    /**
     * RTM实例 {@link IServiceTextChat}
     */
    readonly rtm: IServiceTextChat | null;

    /**
     * 发送消息
     * @param message
     * @returns
     */
    sendMessage: (type: CustomType, message: PeerMsgType) => Promise<boolean>;

    /**
     * 消息表情列表
     */
    reactionList: Map<string, any>;

    /**
     * 更新表情列表
     */
    updateReactionList: (reactionList: Map<string, any>) => void;
    /**
     * 获取某个会话的消息表情列表
     * @param conversationID
     * @returns
     */
    getReactionList: (conversationID: string) => Promise<Map<string, any>>;

    /**
     * 更新当前会话的消息表情列表
     * @param messageId:消息实例ID
     * @param reaction:表情
     * @returns
     */
    updateMessageReaction: (
        messageId: string,
        reaction: string,
        userID: string,
        userName: string,
    ) => Promise<void>;

    /**
     * 初始化
     */
    init: () => Promise<void>;
    /**
     * 初始化仓库
     */
    initStore(config: InitChatStoreConfig): ChatStoreState;
    /**
     * 销毁操作
     */
    destroy(): Promise<void>;
}

export const useChatStore = create<ChatStoreState>((set, get) => {
    const initRTM = async (): Promise<void> => {
        const { sideEffect, rtm } = get();
        if (!rtm) {
            throw new Error("Missing rtm instance");
        }
        sideEffect.addDisposer(
            rtm.events.on("sdk-ready", async () => {
                console.log("RTM SDK is ready, performing initialization...");
            }),
        );
        sideEffect.addDisposer(
            rtm.events.on("sdk-not-ready", () => {
                console.log("RTM SDK is not ready.");
                // 处理 SDK 未准备就绪的情况
            }),
        );
    };
    return {
        sideEffect: new SideEffectManager(),
        toUserUUID: "",
        userUUID: "",
        rtm: null,
        reactionList: new Map(),

        async init() {
            console.log("init rtm");

            await initRTM();
        },
        sendMessage: async (type: CustomType, message: PeerMsgType) => {
            const { rtm, toUserUUID } = get();
            if (!rtm) {
                throw new Error("Missing rtm instance");
            }
            const sendMsg = await rtm.sendPeerMessage(type, toUserUUID, message);
            return Boolean(sendMsg);
        },
        updateReactionList: async (reactionList: Map<string, any>) => {
            set({ reactionList: new Map(Object.entries(reactionList)) });
        },
        updateMessageReaction: async (
            messageId: string,
            reaction: string,
            userID: string,
            userName: string,
        ) => {
            const { rtm, reactionList } = get();
            if (!rtm) {
                throw new Error("Missing rtm instance");
            }
            const newReactionsMap = new Map(reactionList);
            const msgReaction = reactionList.get(messageId) || {};
            const emojiReactions = msgReaction[reaction] || {};
            if (emojiReactions[userID]) {
                // 若已存在该用户的该反应表情，则取消该反应表情
                delete emojiReactions[userID];
                // 若该表情反应队列已为空，则删除
                if (Object.keys(emojiReactions).length === 0) {
                    delete msgReaction[reaction];
                }
            } else {
                emojiReactions[userID] = userName;
            }
            // 若更新后该表情队列不为空，则更新值
            if (msgReaction[reaction] || Object.keys(emojiReactions).length > 0) {
                msgReaction[reaction] = emojiReactions;
            }
            if (Object.keys(msgReaction).length === 0) {
                newReactionsMap.delete(messageId);
            } else {
                newReactionsMap.set(messageId, msgReaction);
            }
            console.log("[WB DEBUG]更新后的消息反应", newReactionsMap);
            set({ reactionList: newReactionsMap });
        },
        getReactionList: async (conversationID: string): Promise<Map<string, any>> => {
            const curMap = new Map();
            console.log("[WEB DEBUG]当前会话消息反应", conversationID);
            // cspell:disable
            curMap.set("144115248064614332-1751443836-94558182", {
                "😄": {
                    "0efc2bf9b85a46fba3dd": "Helly",
                    "0cae4c25379842019fc3": "Sanyu",
                },
                "👍🏻": {
                    "0cae4c25379842019fc3": "Sanyu",
                },
            });
            // cspell:enable
            set({ reactionList: curMap });
            console.log("[WEB DEBUG]当前会话消息反应列表", curMap);
            return curMap;
        },
        initStore: (config: InitChatStoreConfig) => {
            const globalStore = useGlobalStore.getState();
            if (!globalStore.userInfo?.user_id) {
                throw new Error("Missing user uuid");
            }
            set({
                userUUID: globalStore.userInfo.user_id,
                rtm: config.rtm,
            });
            return { ...get() };
        },
        destroy: async () => {
            get().sideEffect.flushAll();
            set({
                toUserUUID: "",
                userUUID: "",
                rtm: null,
                reactionList: new Map(),
            });
        },
    };
});

export const chatStore = useChatStore.getState();
